import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import FancySwitch from '@/components/FancySwitch';
import FullScreenButton from '@/components/FullScreenButton';
import Text from '@/components/Text';
import { RootState } from '@/utils/store';

import LightBox from './LightBox';
import ResultImage from './ResultImage';
import ResultsPlaceholder from './ResultsPlaceholder';
import SelectionActions from './SelectionActions';
import UploadImagesToResults from './UploadImagesToResults';

const Box = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;
const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Heading = styled(Text)`
  margin-bottom: 8px;
`;

const ResultsGrid = styled.div<{
  $isPreviewMode?: boolean;
  $isFullScreenMode: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 24px;
  ${({ $isFullScreenMode }) =>
    $isFullScreenMode &&
    css`
      grid-template-columns: repeat(3, 1fr);
    `}
  ${({ $isPreviewMode }) =>
    $isPreviewMode &&
    css`
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    `}
    ${({ $isFullScreenMode, $isPreviewMode }) =>
    $isFullScreenMode &&
    $isPreviewMode &&
    css`
      grid-template-columns: repeat(6, 1fr);
      gap: 12px;
    `}
`;

const FullScreenButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 12px;
`;

const ButtonsHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

interface Props {
  images?: string[];
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
}

const ImageResults: FC<Props> = ({
  images,
  selectedImages,
  setSelectedImages,
  setImages,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  return (
    <>
      <FullScreenButtonHolder>
        <FullScreenButton />
      </FullScreenButtonHolder>
      <Box>
        {!!images?.length && (
          <LightBox
            lightboxIndex={lightboxIndex}
            setLightboxIndex={setLightboxIndex}
            images={images}
          />
        )}
        <Header>
          <div>
            <Heading $type="button">Generated Results</Heading>
            <Text $type="smallHeadline" $color="gray">
              Download or move covers to the selected project for further
              approval
            </Text>
          </div>
          <ButtonsHolder>
            <FancySwitch
              isActive={isPreviewMode}
              onClick={() => setIsPreviewMode((prev) => !prev)}
              text="Preview mode:"
            />
            <UploadImagesToResults setImages={setImages} />
          </ButtonsHolder>
        </Header>
        {!!images?.length && (
          <SelectionActions
            selectedImages={selectedImages}
            imagesCount={images.length}
            setSelectedImages={setSelectedImages}
            onSelectAll={() =>
              setSelectedImages(images?.map((_, index) => index))
            }
          />
        )}
        {!!images?.length ? (
          <ResultsGrid
            $isPreviewMode={isPreviewMode}
            $isFullScreenMode={fullScreenModeEnabled}
          >
            {images.map((image, index) => (
              <ResultImage
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                onLightboxClick={() => setLightboxIndex(index)}
                index={index}
                image={image}
                key={index}
              />
            ))}
          </ResultsGrid>
        ) : (
          <ResultsPlaceholder />
        )}
      </Box>
    </>
  );
};

export default ImageResults;
